import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import API from './apiConfig';

// Token management helpers
function getStoredTokens() {
  return {
    access: localStorage.getItem('access_token'),
    refresh: localStorage.getItem('refresh_token'),
  };
}
function storeTokens(access, refresh) {
  if (access) localStorage.setItem('access_token', access);
  if (refresh) localStorage.setItem('refresh_token', refresh);
}
function clearTokens() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
}

// Axios interceptor: add Bearer token to every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  // Keep withCredentials for cookie fallback on same-origin
  config.withCredentials = true;
  return config;
});

// Axios response interceptor: auto-refresh on 401
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/api/auth/login') &&
      !originalRequest.url?.includes('/api/auth/refresh') &&
      !originalRequest.url?.includes('/api/auth/register')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const { refresh } = getStoredTokens();
      if (!refresh) {
        isRefreshing = false;
        clearTokens();
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API}/api/auth/refresh`, { refresh_token: refresh });
        storeTokens(data.access_token, data.refresh_token);
        processQueue(null, data.access_token);
        originalRequest.headers['Authorization'] = `Bearer ${data.access_token}`;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function formatError(detail) {
  if (detail == null) return 'Ein Fehler ist aufgetreten.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // null = loading, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const { access } = getStoredTokens();
    if (!access) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/api/auth/me`);
      setUser(data.user || data);
    } catch (err) {
      // Only clear tokens on explicit 401 (invalid/expired token)
      // Keep tokens on network errors (502, timeout) - user stays logged in
      if (err.response?.status === 401) {
        clearTokens();
        setUser(false);
      } else {
        // Network error or server temporarily down - keep user logged in with cached state
        const cached = localStorage.getItem('cached_user');
        if (cached) {
          try { setUser(JSON.parse(cached)); } catch { setUser(false); }
        } else {
          setUser(false);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      if (data.needs_2fa) {
        return { success: true, needs_2fa: true, email: data.email };
      }
      // Store tokens from response body
      if (data.access_token) storeTokens(data.access_token, data.refresh_token);
      const u = data.user || data;
      setUser(u);
      localStorage.setItem('cached_user', JSON.stringify(u));
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  };

  const verifyLoginCode = async (email, code) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/verify-login-code`, { email, code });
      if (data.access_token) storeTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      localStorage.setItem('cached_user', JSON.stringify(data.user));
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  };

  const register = async (fields) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/register`, fields);
      if (data.early_bird_pending) {
        return { success: true, early_bird_pending: true, message: data.message };
      }
      if (data.needs_verification) {
        return { success: true, needs_verification: true, email: data.email, message: data.message };
      }
      if (data.access_token) storeTokens(data.access_token, data.refresh_token);
      const u = data.user || data;
      setUser(u);
      localStorage.setItem('cached_user', JSON.stringify(u));
      return { success: true, message: data.message };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  };

  const verifyRegisterCode = async (email, code) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/verify-register-code`, { email, code });
      if (data.access_token) storeTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  };

  const resendCode = async (email, purpose) => {
    try {
      await axios.post(`${API}/api/auth/resend-code`, { email, purpose });
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`);
    } catch { /* ignore */ }
    clearTokens();
    localStorage.removeItem('cached_user');
    setUser(false);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, verifyLoginCode, verifyRegisterCode, resendCode }}>
      {children}
    </AuthContext.Provider>
  );
}
